import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState } from '@y42/angular/util';
import { ErrorHandlerService } from '@y42/error-handling';
import { GitFileChange, GitQuery, GitService } from '@y42/git';
import { UserUIModel } from '@y42/user/models';
import { combineLatest, filter, map, of, switchMap, take, tap } from 'rxjs';

export interface GitCommitDialogData {
  user: {
    full_name: UserUIModel['full_name'];
    profile_pic: UserUIModel['profile_pic'];
  };
  /**
   * This will be `true` after "git migrations" have been applied,
   * and we want to force the user to commit the changes before continuing.
   */
  forceCommit?: boolean;
  /**
   * you can predefine commit message on showing the commit dialog
   */
  initialCommitMessage?: string;

  filePathsToHideInTree?: string[];
}

@UntilDestroy()
@Component({
  selector: 'y42-git-commit-dialog',
  templateUrl: './git-commit-dialog.component.html',
  styleUrls: ['./git-commit-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GitCommitDialogComponent {
  constructor(
    private readonly dialogRef: MatDialogRef<GitCommitDialogComponent>,
    private readonly gitService: GitService,
    private readonly gitQuery: GitQuery,
    private readonly errorHandlerService: ErrorHandlerService,
    @Inject(MAT_DIALOG_DATA) private readonly data: GitCommitDialogData,
  ) {}

  selectedItems: GitFileChange[] = [];

  readonly forceCommit = this.data.forceCommit ?? false;

  readonly form = new FormGroup({
    title: new FormControl(this.data?.initialCommitMessage?.slice(0, 80) ?? '', {
      validators: [Validators.required, Validators.maxLength(80)],
      nonNullable: true,
    }),
    description: new FormControl('', { validators: [Validators.maxLength(300)], nonNullable: true }),
  });

  readonly state = connectState(this, {
    gitOperationOngoing: this.gitQuery.gitOperationOngoing$,
    areConflictsResolved: combineLatest([this.gitQuery.aheadBehind$, this.gitQuery.hasModifiedFiles$]).pipe(
      filter(([{ ahead, behind }, hasChangedFiles]) => ahead > 0 && behind > 0 && !hasChangedFiles),
      take(1),
      tap(() => this.commit()),
    ),
    changes: this.gitQuery.modifiedFilesList$.pipe(
      switchMap(() => this.gitService.getFileChanges()),
      map((changes) => {
        const filePathsToHide: string[] = this.data?.filePathsToHideInTree ?? [];
        if (filePathsToHide.length > 0) {
          return changes.filter((change) => !filePathsToHide.includes(change.path));
        }
        return changes;
      }),
      tap((changes) => {
        this.selectedItems = changes;
      }),
    ),
    activeUser: of(this.data.user),
  });

  async commit() {
    if (this.form.invalid || !this.selectedItems.length) {
      return;
    }

    try {
      const { title, description } = this.form.value;
      const message = `${title}\n${description}`.trim();
      await this.gitService.commit(
        message,
        (this.selectedItems || []).map(({ path }) => path),
      );
      this.dialogRef.close();
    } catch (err) {
      this.errorHandlerService.error('[GitCommitDialogComponent]: Could not commit the changes', {
        err,
        toast: 'Could not commit the changes',
      });
    }
  }
}
